import { ContractCompanySpecificService } from "@/types";
import { Alert, Grid, Group, Stack } from "@mantine/core";
import { Ref, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateOrderFromContractStore } from "@/stores/createOrderFromContractStore";
import { getContractCardDetails, getContractTradeType } from "@/utils/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import { useGetClosedLoopRemainingAmount } from "@/services/Orders";
import { ContractOrderDetailSchema, ContractOrderDetailsFormInput } from "../validation/orderDetails";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useGetCompanySpecificServices } from "@/services/Company";
import { CreateOrderFromContractValues } from "..";
import Text from "@/components/atoms/Text";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import Divider from "@/components/atoms/Divider";
import NumberInput from "@/components/atoms/NumberInput";
import SelectProductMeasureUnit from "@/components/molecules/SelectInputs/SelectProductMeasureUnit";
import TextInput from "@/components/atoms/TextInput";
import dayjs from "dayjs";
import CTA from "@/components/atoms/CTA";
import DatePickerInput from "@/components/atoms/DatePickerInput";

interface Props {
  onValidationSuccess: (data: any) => void;
  onChangeWizardData: (changed: any) => void;
  wizardData: CreateOrderFromContractValues;
  /** The contract's per-order services, with any price the user has edited already applied. */
  services: ContractCompanySpecificService[];
  ref?: Ref<HTMLFormElement>;
}

function DetailsStep({ onValidationSuccess, onChangeWizardData, wizardData, services, ref }: Props) {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [editingService, setEditingService] = useState<{ id: number; originalPrice: number } | null>(null);
  const contract = useCreateOrderFromContractStore((s) => s.contract);
  const contractTradeType = getContractTradeType(contract!, currentCompany);
  const { sellerName, buyerName } = getContractCardDetails(contract!);
  // The services come from the contract creator's catalog, so only the creator may change their price.
  const canEditServicePrices = !!currentCompany && contract?.createdByCompany === currentCompany.companyId;

  // Only used to look up each service's name and price unit — the price itself comes from `services`.
  const { data: companySpecificServicesData } = useGetCompanySpecificServices(
    contract?.createdByCompany as number,
    false,
    {
      enabled: !!contract?.createdByCompany,
    }
  );

  const setServicePrice = (serviceId: number, price: number) => {
    onChangeWizardData({ servicePriceOverrides: { ...wizardData.servicePriceOverrides, [serviceId]: price } });
  };

  const toggleServiceEdit = (service: ContractCompanySpecificService) => {
    if (editingService?.id === service.companySpecificServiceId) {
      setServicePrice(editingService.id, editingService.originalPrice);
      setEditingService(null);
    } else {
      setEditingService({ id: service.companySpecificServiceId, originalPrice: service.price });
    }
  };

  const { data: closedLoopRemainingAmountData } = useGetClosedLoopRemainingAmount(
    contract?.contractProduct.companySpecificProduct.id as number,
    {
      enabled: !!contract,
    }
  );

  const { handleSubmit, control } = useForm<ContractOrderDetailsFormInput>({
    resolver: zodResolver(
      ContractOrderDetailSchema(
        closedLoopRemainingAmountData?.amountLeftInTons,
        closedLoopRemainingAmountData?.isLoopActive
      )
    ),
    values: {
      amount: wizardData.amount,
      unit: wizardData.unit,
      created: dayjs(wizardData.created).toDate(),
      batchNumber: wizardData.batchNumber ?? "",
    },
  });

  const onSubmit = async (data: ContractOrderDetailsFormInput) => {
    onChangeWizardData(data);
    onValidationSuccess(data);
  };

  return (
    <form ref={ref} data-testid="create-order-details-form" onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack>
            <Text size="sm" color="secondary" data-testid={"product-label"}>
              {t("product:product")}
            </Text>
            <Text size="lg" strong={true} lineClamp={2} data-testid={"product-name"}>
              {contract?.contractProduct.companySpecificProduct.companySpecificProductName}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack>
            <Text size="sm" color="secondary" data-testid={"contract-label"}>
              {t("contracts:contract")}
            </Text>
            <Text size="lg" strong={true} lineClamp={2} data-testid={"contract-name"}>
              {contract?.name}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack>
            <Text size="sm" color="secondary" data-testid={"trading-partner-label"}>
              {t("contracts:tradingPartner")}
            </Text>
            <Text size="lg" strong={true} lineClamp={2} data-testid={"trading-partner-name"}>
              {contractTradeType === ContractTradeTypeEnum.BUY ? sellerName : buyerName}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Group gap={0} align="flex-start">
            <Controller
              control={control}
              name="amount"
              render={({ field, fieldState }) => (
                <NumberInput
                  flex={1}
                  data-testid="order-amount-input"
                  label={t("common:amount")}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              name="unit"
              render={({ field: { onChange, value }, fieldState }) => (
                <SelectProductMeasureUnit
                  disabled
                  id="create-contract-unit"
                  label={t("common:unit")}
                  onChange={(value) => onChange(value ? Number(value) : null)}
                  value={value?.toString()}
                  maw={100}
                  placeholder={t("enumerables:weightUnits")}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  valueKey={"id"}
                  labelKey={"value"}
                />
              )}
            />
          </Group>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="created"
            render={({ field: { onChange, value } }) => {
              return <DatePickerInput label={t("contracts:orderDate")} value={value} onChange={onChange} />;
            }}
          />
        </Grid.Col>

        {closedLoopRemainingAmountData?.isLoopActive ? (
          <>
            {closedLoopRemainingAmountData.isError ? (
              <Alert color="error" title={t("validation:dataFetchingErrors.closedLoopAmountFetchError")}>
                {t("validation:dataFetchingErrors.tryAgainAndContactSuportIfIssuePersist")}
              </Alert>
            ) : (
              <Grid.Col span={12}>
                <Text>
                  {t("contracts:createOrderFromContract.closedLoopRemainingAmount", {
                    amount: closedLoopRemainingAmountData.amountLeftInTons,
                  })}
                </Text>
              </Grid.Col>
            )}
          </>
        ) : null}

        <Grid.Col span={{ base: 12, md: 8 }}>
          <Controller
            control={control}
            name="batchNumber"
            render={({ field }) => <TextInput label={t("common:batchNumber")} {...field} />}
          />
        </Grid.Col>

        {services.length > 0 && (
          <>
            <Grid.Col span={12}>
              <Group gap="md" wrap="wrap">
                {services.map((service) => {
                  const _serviceDetails = companySpecificServicesData?.find(
                    (s) => s.id === service.companySpecificServiceId
                  );
                  const isEditing = editingService?.id === service.companySpecificServiceId;

                  return (
                    <Group
                      key={service.companySpecificServiceId}
                      data-testid={`service-${service.companySpecificServiceId}`}
                      gap="sm"
                      align="center"
                      style={{
                        padding: "10px",
                        border: "1px solid var(--color-border-secondary)",
                        borderRadius: "8px",
                        backgroundColor: "var(--color-background-secondary)",
                        flexWrap: "wrap",
                        flexShrink: 0,
                        flexGrow: 0,
                        minWidth: "150px",
                        width: "fit-content",
                      }}
                    >
                      <Stack gap={0} style={{ flex: "1 1 auto" }}>
                        <Text size="sm" color="secondary">
                          + {_serviceDetails?.name}
                        </Text>
                        {isEditing ? (
                          <Group gap="xs" align="center">
                            <NumberInput
                              style={{ width: "70px" }}
                              size="xs"
                              min={0}
                              data-testid={`service-price-input-${service.companySpecificServiceId}`}
                              defaultValue={service.price}
                              onBlur={() => setEditingService(null)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  // Keep Enter from submitting the step form and advancing the wizard.
                                  e.preventDefault();
                                  setEditingService(null);
                                }
                              }}
                              onChange={(value) => {
                                if (typeof value === "number") {
                                  setServicePrice(service.companySpecificServiceId, value);
                                }
                              }}
                            />
                            <Text size="sm">
                              ({service.currencyType}/
                              {t("enumerables:invoicePaymentIntervals." + _serviceDetails?.priceUnit.code)})
                            </Text>
                          </Group>
                        ) : (
                          <Text size="sm">
                            {service.price} {service.currencyType}/
                            {t("enumerables:invoicePaymentIntervals." + _serviceDetails?.priceUnit.code)}
                          </Text>
                        )}
                      </Stack>

                      {canEditServicePrices && (
                        <CTA
                          intent="text"
                          size="xs"
                          data-testid={`service-edit-${service.companySpecificServiceId}`}
                          // Don't steal focus from the price input: its blur would close edit mode before this
                          // click lands, turning Cancel into a re-open.
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => toggleServiceEdit(service)}
                        >
                          {isEditing ? t("actions:cancel") : t("actions:change")}
                        </CTA>
                      )}
                    </Group>
                  );
                })}
              </Group>
            </Grid.Col>
          </>
        )}
      </Grid>
    </form>
  );
}

export default DetailsStep;
